import { Card, CardContent, CardHeader } from "@ui/components/ui/card.tsx";
import React, { useState } from "react";
import { Label } from "@ui/components/ui/label.tsx";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
} from "@ui/components/ui/select.tsx";
import { CompanySettings, FieldVisibility } from "@elton/types";
import { T } from "@transifex/react";
import { LoadingButton } from "@/components/buttons/loading-button.tsx";
import { useUpdateCompanySettings } from "@/api/mutations/company-settings/update-company-settings.ts";

interface IProps {
  data: {
    settings: CompanySettings;
    companyId: string;
  };
}

export const ChargingNoteSettingsCard: React.FC<IProps> = ({ data }) => {
  const [regNumField, setRegNumField] = useState<FieldVisibility>(data.settings.regNumberField);
  const [noteField, setNoteField] = useState<FieldVisibility>(data.settings.noteField);

  const updateCompanySettings = useUpdateCompanySettings(data.companyId);

  function mapVisibilityEnumToStrings(visibility: FieldVisibility) {
    switch (visibility) {
      case FieldVisibility.HIDDEN:
        return <T _str="Hidden" />;
      case FieldVisibility.OPTIONAL:
        return <T _str="Optional" />;
      case FieldVisibility.REQUIRED:
        return <T _str="Required" />;
      default:
        return <T _str="Optional" />;
    }
  }

  return (
    <Card className="md:max-w-min">
      <CardHeader>
        <h3 className="text-md font-medium">
          <T _str="References on charging sessions" />
        </h3>
        <p className="text-sm text-muted-foreground text-pretty">
          <T _str="Customize what employees need to fill in when they start a charging session." />
        </p>
      </CardHeader>
      <CardContent>
        <div className="md:flex-row flex-col flex wrap gap-4 ">
          <form>
            <Label className="ml-1">
              <T _str="Registration number" />
            </Label>
            <Select onValueChange={(value: FieldVisibility) => setRegNumField(value)}>
              <SelectTrigger className="w-[200px]">
                <span className="capitalize">{mapVisibilityEnumToStrings(regNumField)}</span>
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {Object.values(FieldVisibility).map((value, i) => (
                    <SelectItem key={i} value={value}>
                      {mapVisibilityEnumToStrings(value)}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </form>
          <form>
            <Label className="ml-1">
              <T _str="Free text" />
            </Label>
            <Select onValueChange={(value: FieldVisibility) => setNoteField(value)}>
              <SelectTrigger className="w-[200px]">
                <span className="capitalize">{mapVisibilityEnumToStrings(noteField)}</span>
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {Object.values(FieldVisibility).map((value: FieldVisibility, i) => (
                    <SelectItem key={i} value={value}>
                      {mapVisibilityEnumToStrings(value)}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </form>
        </div>
        <div className="flex w-full justify-end mt-4">
          <LoadingButton
            disabled={
              data.settings.noteField === noteField && data.settings.regNumberField === regNumField
            }
            onClick={() =>
              updateCompanySettings.mutateAsync({
                noteField: noteField,
                regNumberField: regNumField,
                invoiceSpecification: data.settings.invoiceSpecification,
              })
            }
            loading={updateCompanySettings.isPending}
          >
            <T _str="Save" />
          </LoadingButton>
        </div>
      </CardContent>
    </Card>
  );
};
